// src/prototype/components/sep/SEPSectionInitiativePortfolio.tsx
// Component 4: initiative_portfolio

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, Clock, Target, Award } from 'lucide-react';
import { safeNumber, type MappedInitiativePortfolioRow } from '../../utils/mapStrategicExecutionPlan';

export interface SEPSectionInitiativePortfolioProps {
  initiatives: MappedInitiativePortfolioRow[];
  clientName: string;
}

const complexityChip = (complexity: string) => ({
  Low: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
  Medium: 'bg-amber-50 text-amber-700 border border-amber-100',
  High: 'bg-rose-50 text-rose-600 border border-rose-100',
  low: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
  medium: 'bg-amber-50 text-amber-700 border border-amber-100',
  high: 'bg-rose-50 text-rose-600 border border-rose-100',
})[complexity] ?? 'bg-gray-100 text-gray-600 border border-gray-200';

const fmt = (n: number | null | undefined) => `$${safeNumber(n).toFixed(1)}M`;

const InitiativeRow: React.FC<{ initiative: MappedInitiativePortfolioRow }> = ({ initiative }) => {
  const [expanded, setExpanded] = useState(false);
  // ROI Range always mirrors Conservative–Aggressive scenario cards
  const roiRangeLow = safeNumber(initiative.scenarios.conservative.roiHigh);
  const roiRangeHigh = safeNumber(initiative.scenarios.aggressive.roiHigh);
  const roiRangeLabel = `$${roiRangeLow.toFixed(1)}–${roiRangeHigh.toFixed(1)}${initiative.roiEstimate.unit}`;
  return (
    <div className="border border-[#e5e7eb] rounded-xl overflow-hidden bg-white hover:shadow-sm transition-shadow">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full text-left p-4 flex items-center gap-3"
      >
        <div className="w-7 h-7 rounded-full bg-[#18365E] flex items-center justify-center shrink-0">
          <span className="text-[11px] font-bold text-white">{initiative.index}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#18365E] truncate">{initiative.title}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${complexityChip(initiative.complexity)}`}>
              {initiative.complexity}
            </span>
            {initiative.valueBand && (
              <span className="text-[10px] font-medium text-[#18365E]/70 bg-[#18365E]/5 px-2 py-0.5 rounded-full border border-[#18365E]/10">
                {initiative.valueBand}
              </span>
            )}
            <span className="text-[10px] text-[#717182]">{initiative.category}</span>
            <span className="text-[10px] text-[#717182]">Rank #{initiative.rank}</span>
          </div>
        </div>
        <div className="text-right shrink-0 mr-2">
          <p className="text-sm font-bold text-[#FF6E4C]">{roiRangeLabel}</p>
          <p className="text-[10px] text-[#717182]">{initiative.timeToValue}</p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#717182] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#717182] shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-[#e5e7eb] p-4 bg-[#F5F7FA] space-y-4">
          <div>
            <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-1.5">Why this initiative</p>
            <p className="text-xs text-[#18365E]/80 leading-relaxed whitespace-pre-line">{initiative.executionRationale}</p>
          </div>

          <div>
            <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">Scenario Projections</p>
            <div className="grid grid-cols-3 gap-2">
              {(['conservative', 'base', 'aggressive'] as const).map(s => {
                const data = initiative.scenarios[s];
                const isBase = s === 'base';
                return (
                  <div key={s} className={`rounded-lg p-2.5 text-center border ${isBase ? 'bg-[#18365E]/[0.06] border-[#18365E]/15' : 'bg-white border-[#e5e7eb]'}`}>
                    <p className="text-[10px] font-semibold text-[#717182] capitalize mb-1">{s === 'base' ? 'Base Case' : s.charAt(0).toUpperCase() + s.slice(1)}</p>
                    <p className={`text-sm font-bold ${isBase ? 'text-[#FF6E4C]' : 'text-[#18365E]'}`}>{fmt(data.roiHigh)}</p>
                    <p className="text-[10px] text-[#717182]">{data.paybackMonths}mo payback</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: <TrendingUp className="w-3 h-3 text-[#FF6E4C]" />, label: 'ROI Range', value: roiRangeLabel },
              { icon: <Clock className="w-3 h-3 text-[#18365E]" />, label: 'Time to Value', value: initiative.timeToValue },
              { icon: <Target className="w-3 h-3 text-[#18365E]" />, label: 'Confidence', value: `${initiative.validationScore}%` },
            ].map(({ icon, label, value }) => (
              <div key={label} className="bg-white border border-[#e5e7eb] rounded-lg p-2 text-center">
                <div className="flex justify-center mb-1">{icon}</div>
                <p className="text-[9px] font-semibold text-[#717182] uppercase tracking-wide">{label}</p>
                <p className="text-xs font-bold text-[#18365E]">{value}</p>
              </div>
            ))}
          </div>

          {initiative.valueBand && (
            <div className="flex items-center gap-2 text-xs text-[#18365E]/80">
              <Award className="w-3.5 h-3.5 text-[#FF6E4C] shrink-0" />
              <span><strong>Value Category:</strong> {initiative.valueBand}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SEPSectionInitiativePortfolio: React.FC<SEPSectionInitiativePortfolioProps> = ({ initiatives, clientName }) => (
  <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
      <div>
        <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 4</p>
        <h2 className="text-lg font-bold text-[#18365E] leading-tight">Initiative Portfolio</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">Initiative-level rationale, scenarios, and execution metrics</p>
      </div>
    </div>

    <p className="text-sm text-[#18365E]/80 leading-relaxed mb-5">
      The following initiatives have been approved for execution planning at <strong>{clientName}</strong>.
      Expand each initiative to review rationale, scenario projections, and key execution metrics.
    </p>

    <div className="space-y-3">
      {initiatives.map(init => <InitiativeRow key={init.id} initiative={init} />)}
    </div>
  </div>
);

export default SEPSectionInitiativePortfolio;
